import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Trans } from "@lingui/macro";

interface OverlapDetailsProps {
  onClose?: () => void;
  data?: any;
  inspectorName?: string;
}

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

const formatDateRange = (
  min: string | null | undefined,
  max: string | null | undefined,
): string => {
  if (!min && !max) return "N/A";
  const fMin = formatDate(min);
  const fMax = formatDate(max);
  return fMin === fMax ? fMin : `${fMin} → ${fMax}`;
};

const OverlapDetails: React.FC<OverlapDetailsProps> = ({
  onClose,
  data,
  inspectorName,
}) => {
  const groups = data?.groups || [];
  const summary = data?.summary || {};

  const uniqueInspections = React.useMemo(() => {
    const map = new Map<number, any>();
    for (const group of groups) {
      for (const inspRange of group.inspection_ranges || []) {
        if (inspRange.inspection_id === data?.inspection_id) continue;
        if (!map.has(inspRange.inspection_id)) {
          map.set(inspRange.inspection_id, { ...inspRange });
        } else {
          const existing = map.get(inspRange.inspection_id);
          existing.overlapped_activities_count +=
            inspRange.overlapped_activities_count;
          const existMin = existing.overlapped_activities_date_range?.min;
          const existMax = existing.overlapped_activities_date_range?.max;
          const newMin = inspRange.overlapped_activities_date_range?.min;
          const newMax = inspRange.overlapped_activities_date_range?.max;
          if (newMin && (!existMin || newMin < existMin))
            existing.overlapped_activities_date_range.min = newMin;
          if (newMax && (!existMax || newMax > existMax))
            existing.overlapped_activities_date_range.max = newMax;
        }
      }
    }
    return Array.from(map.values());
  }, [groups, data?.inspection_id]);

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: "bold", color: "black" }}>
            <Trans>Overlap details</Trans> &mdash; <Trans>Inspection</Trans>{" "}
            INSP-{data?.inspection_id || "N/A"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            <Trans>Inspector</Trans>: {inspectorName || "N/A"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Table */}
      <TableContainer>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  color: "text.secondary",
                  borderBottom: "none",
                }}
              >
                <Trans>INSPECTION</Trans>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  color: "text.secondary",
                  borderBottom: "none",
                }}
              >
                <Trans>PERIOD</Trans>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  color: "text.secondary",
                  borderBottom: "none",
                }}
              >
                <Trans>DAYS OVERLAPPING</Trans>
              </TableCell>
              <TableCell sx={{ borderBottom: "none" }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {uniqueInspections.map((inspRange: any) => {
              const periodRange = formatDateRange(
                inspRange.all_activities_date_range?.min,
                inspRange.all_activities_date_range?.max,
              );
              const overlappedRange = formatDateRange(
                inspRange.overlapped_activities_date_range?.min,
                inspRange.overlapped_activities_date_range?.max,
              );

              return (
                <TableRow key={inspRange.inspection_id}>
                  <TableCell sx={{ py: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      INSP-{inspRange.inspection_id}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 2 }}>
                    <Typography variant="body2">{periodRange}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {inspRange.total_activities_count}{" "}
                      <Trans>activities total</Trans>
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 2 }}>
                    <Typography variant="body2">
                      {inspRange.overlapped_activities_count}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {overlappedRange}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 2 }}>
                    <Button
                      size="small"
                      variant="text"
                      sx={{ fontWeight: 500 }}
                      href={`/activities?insp=${inspRange.inspection_id}`}
                      target="_blank"
                    >
                      <Trans>OPEN</Trans>
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer Text */}
      <Box sx={{ mt: 3, mb: 3, px: 1 }}>
        <Typography variant="body2">
          <b>
            {(summary.target_overlapped_activities_count || 0) +
              (summary.target_primary_activities_count || 0)}{" "}
            <Trans>days</Trans>
          </b>{" "}
          <Trans>on this inspection are covered by other inspections and</Trans>{" "}
          <b>
            <Trans>will not be priced</Trans>
          </b>{" "}
          <Trans>in the export. The</Trans>{" "}
          <Box
            component="span"
            sx={{
              backgroundColor: "#f0f0f0",
              px: 0.5,
              py: 0.2,
              borderRadius: 1,
              fontFamily: "monospace",
            }}
          >
            <Trans>Overlap</Trans>
          </Box>{" "}
          <Trans>flag has been auto-set on those activities.</Trans>
        </Typography>
      </Box>

      {/* Bottom Actions */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1 }}>
        <Button onClick={onClose} variant="text" sx={{ fontWeight: "bold" }}>
          <Trans>CLOSE</Trans>
        </Button>
      </Box>
    </Box>
  );
};

export default OverlapDetails;
